import { supabase } from '../config/supabase';
import { AnalyticsConsentService } from './AnalyticsConsentService';

// ==================== INTERFACES ====================

export interface PageView {
  id: string;
  user_id: string;
  page_path: string;
  page_title: string;
  session_id: string | null;
  time_spent_seconds: number;
  entered_at: string;
  exited_at: string | null;
  created_at: string;
}

export interface UserEngagementEvent {
  id: string;
  user_id: string;
  event_type: 'trade_import' | 'file_upload' | 'report_generated' | 'custom_column_created' |
              'dashboard_created' | 'diary_entry' | 'chart_viewed' | 'settings_changed';
  event_metadata: Record<string, any>;
  session_id: string | null;
  created_at: string;
}

export interface UserAnalytics {
  user_id: string;
  user_email: string;
  total_sessions: number;
  total_time_minutes: number;
  avg_session_minutes: number;
  last_active: string;
  most_visited_page: string;
  engagement_score: number;
  total_events: number;
}

export interface PageAnalytics {
  page_path: string;
  total_views: number;
  unique_visitors: number;
  avg_time_seconds: number;
  total_time_minutes: number;
}

export interface DailyUsageStats {
  date: string;
  total_users: number;
  total_sessions: number;
  avg_session_minutes: number;
  total_page_views: number;
  total_events: number;
}

export interface UserEngagementFull {
  user_id: string;
  user_email: string;
  total_sessions: number;
  total_time_minutes: number;
  avg_session_minutes: number;
  last_active: string;
  files_uploaded: number;
  trades_imported: number;
  reports_generated: number;
  comparisons_created: number;
  diary_entries: number;
  custom_columns: number;
  last_file_upload: string | null;
  last_report: string | null;
  last_diary: string | null;
  last_comparison: string | null;
  features_used: number;
  engagement_score: number;
  is_active_user: boolean;
}

export interface FeatureAdoptionStats {
  feature_name: string;
  users_count: number;
  total_usage: number;
  avg_per_user: number;
  adoption_rate: number;
}

// ==================== ANALYTICS SERVICE ====================

export class AnalyticsService {
  private static currentPageViewId: string | null = null;
  private static currentPagePath: string | null = null;
  private static pageEnterTime: Date | null = null;
  private static sessionId: string | null = null;

  // ==================== PAGE TRACKING ====================

  /**
   * Start tracking a page view
   */
  static async trackPageEnter(
    userId: string,
    pagePath: string,
    pageTitle: string,
    sessionId?: string
  ): Promise<void> {
    try {
      // End previous page view if exists
      if (this.currentPageViewId) {
        await this.trackPageExit();
      }

      this.sessionId = sessionId || null;
      this.currentPagePath = pagePath;
      this.pageEnterTime = new Date();

      const { data, error } = await supabase
        .from('page_views')
        .insert({
          user_id: userId,
          page_path: pagePath,
          page_title: pageTitle,
          session_id: this.sessionId,
          entered_at: this.pageEnterTime.toISOString(),
          time_spent_seconds: 0
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error tracking page enter:', error);
        return;
      }

      this.currentPageViewId = data.id;
    } catch (error) {
      console.error('Error tracking page enter:', error);
    }
  }

  /**
   * End tracking current page view
   */
  static async trackPageExit(): Promise<void> {
    if (!this.currentPageViewId || !this.pageEnterTime) return;

    try {
      const exitTime = new Date();
      const timeSpentSeconds = Math.floor((exitTime.getTime() - this.pageEnterTime.getTime()) / 1000);

      await supabase
        .from('page_views')
        .update({
          exited_at: exitTime.toISOString(),
          time_spent_seconds: timeSpentSeconds
        })
        .eq('id', this.currentPageViewId);

      this.currentPageViewId = null;
      this.currentPagePath = null;
      this.pageEnterTime = null;
    } catch (error) {
      console.error('Error tracking page exit:', error);
    }
  }

  /**
   * Update time spent on current page (heartbeat)
   */
  static async updatePageTime(): Promise<void> {
    if (!this.currentPageViewId || !this.pageEnterTime) return;

    try {
      const now = new Date();
      const timeSpentSeconds = Math.floor((now.getTime() - this.pageEnterTime.getTime()) / 1000);

      await supabase
        .from('page_views')
        .update({
          time_spent_seconds: timeSpentSeconds
        })
        .eq('id', this.currentPageViewId);
    } catch (error) {
      console.error('Error updating page time:', error);
    }
  }

  // ==================== ENGAGEMENT TRACKING ====================

  /**
   * Track a user engagement event
   */
  static async trackEvent(
    userId: string,
    eventType: UserEngagementEvent['event_type'],
    metadata: Record<string, any> = {},
    sessionId?: string
  ): Promise<void> {
    // Guard the arguments before spending a round trip. A caller that passed an
    // object where a user id belongs shipped event_type: undefined to the
    // database for months; the insert was rejected and the next line still
    // announced success, so the failure was invisible in the console.
    if (!userId || typeof userId !== 'string') {
      console.error('AnalyticsService.trackEvent: userId must be a string, got', typeof userId, userId);
      return;
    }
    if (!eventType || typeof eventType !== 'string') {
      console.error('AnalyticsService.trackEvent: eventType is required, got', eventType);
      return;
    }

    try {
      // supabase-js reports failures in the result rather than throwing, so the
      // surrounding try/catch never saw them. Read the error.
      const { error } = await supabase
        .from('user_engagement_events')
        .insert({
          user_id: userId,
          event_type: eventType,
          event_metadata: metadata,
          session_id: sessionId || this.sessionId
        });

      if (error) {
        console.error(`Error tracking event "${eventType}":`, error.message, error);
        return;
      }

      console.log(`Event tracked: ${eventType}`, metadata);

      const { UnifiedAnalyticsService } = await import('./UnifiedAnalyticsService');
      await UnifiedAnalyticsService.trackOpsEvent('feature_used', {
        feature: eventType,
        ...metadata
      }, userId);
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }

  // ==================== CONVENIENCE EVENT TRACKERS ====================

  static async trackTradeImport(userId: string, fileCount: number, tradeCount: number): Promise<void> {
    await this.trackEvent(userId, 'trade_import', { fileCount, tradeCount });
  }

  static async trackFileUpload(userId: string, fileName: string, fileSize: number): Promise<void> {
    await this.trackEvent(userId, 'file_upload', { fileName, fileSize });
  }

  static async trackReportGenerated(userId: string, reportType: string): Promise<void> {
    await this.trackEvent(userId, 'report_generated', { reportType });
  }

  static async trackCustomColumnCreated(userId: string, columnName: string, columnType: string): Promise<void> {
    await this.trackEvent(userId, 'custom_column_created', { columnName, columnType });
  }

  static async trackDashboardCreated(userId: string, dashboardName: string): Promise<void> {
    await this.trackEvent(userId, 'dashboard_created', { dashboardName });
  }

  static async trackDiaryEntry(userId: string, entryDate: string): Promise<void> {
    await this.trackEvent(userId, 'diary_entry', { entryDate });
  }

  static async trackChartViewed(userId: string, chartType: string): Promise<void> {
    await this.trackEvent(userId, 'chart_viewed', { chartType });
  }

  static async trackSettingsChanged(userId: string, setting: string): Promise<void> {
    await this.trackEvent(userId, 'settings_changed', { setting });
  }

  // ==================== ADMIN ANALYTICS QUERIES ====================

  /**
   * Get analytics for all users (for admin dashboard)
   */
  static async getUserAnalytics(limit: number = 50): Promise<UserAnalytics[]> {
    try {
      const { data, error } = await supabase.rpc('get_user_analytics', { limit_count: limit });

      if (error) {
        console.error('Error fetching user analytics:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching user analytics:', error);
      return [];
    }
  }

  /**
   * Get page analytics (which pages are most visited)
   */
  static async getPageAnalytics(days: number = 7): Promise<PageAnalytics[]> {
    try {
      const { data, error } = await supabase.rpc('get_page_analytics', { days_back: days });

      if (error) {
        console.error('Error fetching page analytics:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching page analytics:', error);
      return [];
    }
  }

  /**
   * Get daily usage statistics
   */
  static async getDailyUsageStats(days: number = 30): Promise<DailyUsageStats[]> {
    try {
      const { data, error } = await supabase.rpc('get_daily_usage_stats', { days_back: days });

      if (error) {
        console.error('Error fetching daily usage stats:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching daily usage stats:', error);
      return [];
    }
  }

  /**
   * Get user engagement timeline (last N events per user)
   */
  static async getUserEngagementTimeline(userId: string, limit: number = 10): Promise<UserEngagementEvent[]> {
    try {
      const { data, error } = await supabase
        .from('user_engagement_events')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching engagement timeline:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching engagement timeline:', error);
      return [];
    }
  }

  /**
   * Get engagement events by type
   */
  static async getEventsByType(days: number = 7): Promise<Record<string, number>> {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from('user_engagement_events')
        .select('event_type')
        .gte('created_at', startDate.toISOString());

      if (error) {
        console.error('Error fetching events by type:', error);
        return {};
      }

      // Count events by type
      const counts: Record<string, number> = {};
      data.forEach((event: { event_type: string }) => {
        counts[event.event_type] = (counts[event.event_type] || 0) + 1;
      });

      return counts;
    } catch (error) {
      console.error('Error fetching events by type:', error);
      return {};
    }
  }

  /**
   * Get top active users
   */
  static async getTopActiveUsers(limit: number = 5): Promise<UserAnalytics[]> {
    try {
      const allUsers = await this.getUserAnalytics(100);
      return allUsers
        .sort((a, b) => b.engagement_score - a.engagement_score)
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching top active users:', error);
      return [];
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Initialize page tracking with automatic updates
   */
  static initPageTracking(userId: string): void {
    // Update page time every 30 seconds
    setInterval(() => {
      this.updatePageTime();
    }, 30000);

    // Track page exit on beforeunload
    window.addEventListener('beforeunload', () => {
      this.trackPageExit();
    });

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.updatePageTime();
      }
    });
  }

  /**
   * Get formatted page title from path
   */
  static getPageTitle(path: string): string {
    const titles: Record<string, string> = {
      '/': 'Dashboard',
      '/landing': 'Landing Page',
      '/charts': 'Charts',
      '/data': 'Data Files',
      '/diary': 'Trading Diary',
      '/report': 'Trading Report',
      '/settings': 'Settings',
      '/platforms': 'Platforms'
    };

    return titles[path] || path;
  }

  // ==================== ADVANCED FEATURE TRACKING ====================

  /**
   * Track file upload (calls Supabase function)
   */
  static async trackFileUploadDB(userId: string, tradeCount: number = 0): Promise<void> {
    try {
      const { error } = await supabase.rpc('track_file_upload', {
        p_user_id: userId,
        p_trade_count: tradeCount
      });

      if (error) {
        console.error('Error tracking file upload:', error);
      } else {
        console.log(`✅ Tracked file upload: ${tradeCount} trades`);
      }

      const { UnifiedAnalyticsService } = await import('./UnifiedAnalyticsService');
      await UnifiedAnalyticsService.trackDataFileUploaded({
        userId,
        tradeCount,
        source: 'data_table'
      });
    } catch (error) {
      console.error('Error tracking file upload:', error);
    }
  }

  /**
   * Track report generation (calls Supabase function)
   */
  static async trackReportGeneratedDB(userId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('track_report_generated', {
        p_user_id: userId
      });

      if (error) {
        console.error('Error tracking report:', error);
      } else {
        console.log('✅ Tracked report generation');
      }

      const { UnifiedAnalyticsService } = await import('./UnifiedAnalyticsService');
      await UnifiedAnalyticsService.trackReportGenerated({
        userId,
        source: 'report_builder'
      });
    } catch (error) {
      console.error('Error tracking report:', error);
    }
  }

  /**
   * Track comparison creation (calls Supabase function)
   */
  static async trackComparisonCreatedDB(userId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('track_comparison_created', {
        p_user_id: userId
      });

      if (error) {
        console.error('Error tracking comparison:', error);
      } else {
        console.log('✅ Tracked comparison creation');
      }
    } catch (error) {
      console.error('Error tracking comparison:', error);
    }
  }

  /**
   * Track diary entry (calls Supabase function)
   */
  static async trackDiaryEntryDB(userId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('track_diary_entry', {
        p_user_id: userId
      });

      if (error) {
        console.error('Error tracking diary entry:', error);
      } else {
        console.log('✅ Tracked diary entry');
      }

      const { UnifiedAnalyticsService } = await import('./UnifiedAnalyticsService');
      await UnifiedAnalyticsService.trackDiaryEntryCreated({
        userId,
        source: 'diary_editor'
      });
    } catch (error) {
      console.error('Error tracking diary entry:', error);
    }
  }

  /**
   * Track custom column creation (calls Supabase function)
   */
  static async trackCustomColumnDB(userId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('track_custom_column', {
        p_user_id: userId
      });

      if (error) {
        console.error('Error tracking custom column:', error);
      } else {
        console.log('✅ Tracked custom column creation');
      }

      const { UnifiedAnalyticsService } = await import('./UnifiedAnalyticsService');
      await UnifiedAnalyticsService.trackOpsEvent('feature_used', {
        feature: 'custom_column_created'
      }, userId);
    } catch (error) {
      console.error('Error tracking custom column:', error);
    }
  }

  static async trackReportAiSummaryRequestedDB(
    userId: string,
    provider?: string,
    context?: string
  ): Promise<void> {
    try {
      const { UnifiedAnalyticsService } = await import('./UnifiedAnalyticsService');
      await UnifiedAnalyticsService.trackReportAiSummaryRequested({
        userId,
        provider: provider || null,
        context: context || null,
        source: 'report_ai'
      });
    } catch (error) {
      console.error('Error tracking report AI summary request:', error);
    }
  }

  /**
   * Get comprehensive user engagement with all features
   */
  static async getUserEngagementFull(limit: number = 50): Promise<UserEngagementFull[]> {
    try {
      const { data, error } = await supabase.rpc('get_user_engagement_full', {
        limit_count: limit
      });

      if (error) {
        console.error('Error fetching user engagement:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching user engagement:', error);
      return [];
    }
  }

  /**
   * Get feature adoption statistics
   */
  static async getFeatureAdoptionStats(): Promise<FeatureAdoptionStats[]> {
    try {
      const { data, error } = await supabase.rpc('get_feature_adoption_stats');

      if (error) {
        console.error('Error fetching feature adoption stats:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching feature adoption stats:', error);
      return [];
    }
  }

  // ==================== LANDING PAGE TRACKING ====================

  /**
   * Get or create visitor ID for anonymous landing page tracking
   * Uses localStorage UUID (GDPR-compliant, not IP-based)
   */
  static getOrCreateVisitorId(): string {
    const STORAGE_KEY = 'ts_visitor_id';

    try {
      let visitorId = localStorage.getItem(STORAGE_KEY);

      if (!visitorId) {
        // Generate new UUID
        visitorId = crypto.randomUUID();
        localStorage.setItem(STORAGE_KEY, visitorId);
        console.log('🆔 New visitor ID created:', visitorId);
      }

      return visitorId;
    } catch (error) {
      // Fallback if localStorage not available
      console.error('Error accessing localStorage:', error);
      return `temp_${Date.now()}_${Math.random()}`;
    }
  }

  /**
   * Track landing page view
   */
  static async trackLandingView(visitorId: string): Promise<string | null> {
    if (!AnalyticsConsentService.hasGranted()) {
      return null;
    }

    try {
      // Get metadata
      const metadata = {
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent
      };

      await supabase
        .from('landing_page_events')
        .insert({
          visitor_id: visitorId,
          event_type: 'landing_view',
          event_metadata: {},
          user_agent: metadata.user_agent,
          referrer: metadata.referrer,
          screen_width: metadata.screen_width,
          screen_height: metadata.screen_height,
          page: 'landing'
        });

      // Also track in page_views for time calculation (use visitor_id field for anonymous users)
      const { data, error } = await supabase
        .from('page_views')
        .insert({
          visitor_id: visitorId, // Using visitor_id for anonymous tracking
          user_id: null, // No authenticated user
          page_path: '/landing',
          page_title: 'Landing Page',
          entered_at: new Date().toISOString(),
          time_spent_seconds: 0
        })
        .select('id');

      if (error) {
        console.error('Error storing landing page view row:', error);
      }

      const rowId = Array.isArray(data) && data[0]?.id ? data[0].id : null;

      const { UnifiedAnalyticsService } = await import('./UnifiedAnalyticsService');
      await UnifiedAnalyticsService.trackLandingPageViewed({
        visitorId,
        source: 'landing_page'
      });

      console.log('📊 Landing view tracked:', visitorId);
      return rowId;
    } catch (error) {
      console.error('Error tracking landing view:', error);
      return null;
    }
  }

  static async trackAnonymousPageExit(pageViewId: string): Promise<void> {
    if (!pageViewId) return;

    try {
      const exitTime = new Date().toISOString();

      const { data, error } = await supabase
        .from('page_views')
        .select('entered_at')
        .eq('id', pageViewId)
        .single();

      if (error) {
        console.error('Error loading anonymous page view row:', error);
        return;
      }

      const enteredAt = data?.entered_at ? new Date(data.entered_at) : null;
      const timeSpentSeconds = enteredAt
        ? Math.max(0, Math.floor((Date.now() - enteredAt.getTime()) / 1000))
        : 0;

      await supabase
        .from('page_views')
        .update({
          exited_at: exitTime,
          time_spent_seconds: timeSpentSeconds
        })
        .eq('id', pageViewId);
    } catch (error) {
      console.error('Error tracking anonymous page exit:', error);
    }
  }

  /**
   * Track landing page CTA click
   * @param visitorId - Visitor UUID from localStorage
   * @param ctaPosition - Position of CTA ('hero' or 'footer')
   */
  static async trackLandingCTAClick(
    visitorId: string,
    ctaPosition: 'hero' | 'footer' | 'header' | 'pricing' | 'platforms'
  ): Promise<void> {
    if (!AnalyticsConsentService.hasGranted()) {
      return;
    }

    try {
      await supabase
        .from('landing_page_events')
        .insert({
          visitor_id: visitorId,
          event_type: 'landing_cta_click',
          event_metadata: { cta_position: ctaPosition },
          page: 'landing'
        });

      const { UnifiedAnalyticsService } = await import('./UnifiedAnalyticsService');
      await UnifiedAnalyticsService.trackLandingCtaClick({
        visitorId,
        position: ctaPosition,
        target: 'login',
        source: `landing_${ctaPosition}`
      });

      console.log(`📊 Landing CTA clicked: ${ctaPosition}`, visitorId);
    } catch (error) {
      console.error('Error tracking landing CTA click:', error);
    }
  }

  /**
   * Track landing page scroll depth
   * @param visitorId - Visitor UUID from localStorage
   * @param depth - Scroll depth percentage (25, 50, 75, 100)
   */
  static async trackLandingScroll(
    visitorId: string,
    depth: 25 | 50 | 75 | 100
  ): Promise<void> {
    if (!AnalyticsConsentService.hasGranted()) {
      return;
    }

    try {
      await supabase
        .from('landing_page_events')
        .insert({
          visitor_id: visitorId,
          event_type: 'landing_scroll',
          event_metadata: { depth },
          page: 'landing'
        });

      console.log(`📊 Landing scroll tracked: ${depth}%`, visitorId);
    } catch (error) {
      console.error('Error tracking landing scroll:', error);
    }
  }

  /**
   * Track landing page conversion (signup completed)
   * Call this ONLY on first signup, not every login
   * @param visitorId - Visitor UUID from localStorage
   * @param userId - New user ID from auth (after signup)
   */
  static async trackLandingConversion(
    visitorId: string,
    userId?: string
  ): Promise<void> {
    try {
      await supabase
        .from('landing_page_events')
        .insert({
          visitor_id: visitorId,
          event_type: 'landing_conversion',
          event_metadata: { user_id: userId || null },
          page: 'landing'
        });

      const { UnifiedAnalyticsService } = await import('./UnifiedAnalyticsService');
      await UnifiedAnalyticsService.trackLandingConversion({
        userId: userId || null,
        visitorId,
        source: 'landing_signup'
      });

      console.log('🎉 Landing conversion tracked:', visitorId, '→', userId);
    } catch (error) {
      console.error('Error tracking landing conversion:', error);
    }
  }
}
